/**
 * Admin Dashboard — BMU iç ekibi (S5.3)
 *
 *   /admin/dashboard
 *
 * Tüm sistemin sağlık metrikleri: firma sayısı, sertifika, AI cost, founder slot.
 */
import { useEffect, useState } from 'react';
import { Link, useLocation } from 'wouter';
import { Header } from '../components/Header.js';
import { Alert, Card } from '../components/ui.js';
import { api, ApiError } from '../lib/api.js';
import { useSession } from '../lib/auth-client.js';

interface AdminDashboardData {
  counts: {
    organizations: number;
    activeOrganizations: number;
    users: number;
    activeExams: number;
    attempts: number;
    completedAttempts: number;
    certificates: number;
    approvedQuestions: number;
    draftQuestions: number;
    chatSessions30d: number;
  };
  aiCost30dUsd: number;
  foundersProgram: { used: number; total: number };
  levelDistribution: Array<{ scoreLevel: string | null; count: number }>;
  recentOrgs: Array<{
    id: string;
    name: string;
    industry: string;
    bmuLeadStatus: string;
    createdAt: string;
    _count: { members: number; exams: number };
  }>;
}

export function AdminDashboardPage(): JSX.Element {
  const [, setLocation] = useLocation();
  const { data: session, isPending: sessionPending } = useSession();
  const [data, setData] = useState<AdminDashboardData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (sessionPending) return;
    if (!session?.user) {
      setLocation('/yonetici/giris');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const result = await api.get<AdminDashboardData>('/api/admin/dashboard');
        if (!cancelled) setData(result);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.code === 'FORBIDDEN') {
          setError('Bu sayfa sadece BMU iç ekibine açıktır.');
        } else if (err instanceof ApiError) {
          setError(err.message);
        } else {
          setError('Veriler yüklenemedi');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [session, sessionPending, setLocation]);

  if (sessionPending || (!data && !error)) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <main className="mx-auto max-w-6xl px-6 py-12">
          <p className="text-slate-500">Yükleniyor…</p>
        </main>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <main className="mx-auto max-w-3xl px-6 py-12">
          <Alert variant="danger">{error}</Alert>
        </main>
      </div>
    );
  }

  const foundersPct = Math.round(
    (data.foundersProgram.used / data.foundersProgram.total) * 100,
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="mx-auto max-w-6xl px-6 py-12">
        <div className="mb-8 flex items-end justify-between">
          <div>
            <p className="text-sm font-medium text-purple-700">
              BMU Iç Ekibi
            </p>
            <h1 className="text-3xl font-bold text-slate-900">Sistem Paneli</h1>
          </div>
        </div>

        {/* Hızlı aksiyon kartları */}
        <div className="mb-6 grid gap-3 sm:grid-cols-3">
          <Link
            href="/admin/firmalar"
            className="group rounded-xl border border-slate-200 bg-white p-4 shadow-sm hover:shadow-md"
          >
            <div className="text-2xl">🏢</div>
            <h3 className="mt-2 text-base font-semibold text-slate-900">
              Firma Yönetimi
            </h3>
            <p className="text-xs text-slate-600">
              Yeni firma + owner yarat, liste, detay
            </p>
          </Link>
          <Link
            href="/admin/kullanicilar"
            className="group rounded-xl border border-slate-200 bg-white p-4 shadow-sm hover:shadow-md"
          >
            <div className="text-2xl">👤</div>
            <h3 className="mt-2 text-base font-semibold text-slate-900">
              Kullanıcı Yönetimi
            </h3>
            <p className="text-xs text-slate-600">
              Tüm kullanıcılar, staff toggle, şifre sıfırla
            </p>
          </Link>
          <Link
            href="/admin/sorular"
            className="group rounded-xl border border-slate-200 bg-white p-4 shadow-sm hover:shadow-md"
          >
            <div className="text-2xl">❓</div>
            <h3 className="mt-2 text-base font-semibold text-slate-900">
              Soru Havuzu
            </h3>
            <p className="text-xs text-slate-600">
              AI taslakları, review queue, onay/arşiv
            </p>
          </Link>
        </div>

        {/* Count cards */}
        <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-5">
          <CountCard label="Firma" value={data.counts.organizations} />
          <CountCard label="Aktif Firma" value={data.counts.activeOrganizations} />
          <CountCard label="Kullanıcı" value={data.counts.users} />
          <CountCard label="Aktif Sınav" value={data.counts.activeExams} />
          <CountCard label="Sertifika" value={data.counts.certificates} />
        </div>

        {/* AI cost + founders */}
        <div className="mt-6 grid gap-6 lg:grid-cols-2">
          <Card>
            <h2 className="text-lg font-semibold text-slate-900">
              AI Maliyet (Son 30 Gün)
            </h2>
            <p className="mt-3 text-4xl font-bold text-blue-900">
              ${data.aiCost30dUsd.toFixed(2)}
            </p>
            <p className="mt-1 text-xs text-slate-500">
              Aday rapor + manager rapor toplam (chat dahil değil)
            </p>
            <div className="mt-4 space-y-2 text-sm">
              <Row label="Aday Sınavları" value={data.counts.attempts} />
              <Row label="Tamamlanan" value={data.counts.completedAttempts} />
              <Row label="Chat Session (30g)" value={data.counts.chatSessions30d} />
            </div>
          </Card>

          <Card>
            <h2 className="text-lg font-semibold text-slate-900">
              Kurucu Üyeler Programı (Spec 16.6)
            </h2>
            <p className="mt-3 text-4xl font-bold text-amber-700">
              {data.foundersProgram.used}{' '}
              <span className="text-base text-slate-400">
                / {data.foundersProgram.total}
              </span>
            </p>
            <div className="mt-2 h-2 rounded-full bg-slate-100">
              <div
                className="h-full rounded-full bg-amber-500"
                style={{ width: `${foundersPct}%` }}
              />
            </div>
            <p className="mt-2 text-xs text-slate-500">
              Aktif (COLD olmayan) firma sayısı
            </p>
          </Card>
        </div>

        {/* Question pool */}
        <Card className="mt-6">
          <h2 className="text-lg font-semibold text-slate-900">Soru Havuzu</h2>
          <div className="mt-3 grid gap-4 sm:grid-cols-3">
            <Detail
              label="APPROVED"
              value={String(data.counts.approvedQuestions)}
              highlight
            />
            <Detail
              label="DRAFT (review bekliyor)"
              value={String(data.counts.draftQuestions)}
            />
            <div className="flex items-end justify-end">
              <Link
                href="/admin/sorular"
                className="text-sm font-medium text-blue-900 hover:underline"
              >
                Review Queue →
              </Link>
            </div>
          </div>
        </Card>

        {/* Level distribution */}
        {data.levelDistribution.length > 0 && (
          <Card className="mt-6">
            <h2 className="text-lg font-semibold text-slate-900">
              Tüm Sistem — Seviye Dağılımı
            </h2>
            <div className="mt-3 space-y-2">
              {data.levelDistribution
                .sort((a, b) => b.count - a.count)
                .map((l) => (
                  <Row
                    key={l.scoreLevel ?? 'null'}
                    label={l.scoreLevel ?? '—'}
                    value={l.count}
                  />
                ))}
            </div>
          </Card>
        )}

        {/* Recent orgs */}
        <Card className="mt-6">
          <h2 className="text-lg font-semibold text-slate-900">Son Firmalar</h2>
          {data.recentOrgs.length === 0 ? (
            <p className="mt-3 text-sm text-slate-500">Henüz firma yok</p>
          ) : (
            <ul className="mt-4 divide-y divide-slate-100">
              {data.recentOrgs.map((o) => (
                <li key={o.id} className="flex items-center justify-between py-3">
                  <div>
                    <p className="font-medium text-slate-900">{o.name}</p>
                    <p className="text-xs text-slate-500">
                      {o.industry} ·{' '}
                      {new Date(o.createdAt).toLocaleDateString('tr-TR')} ·{' '}
                      Lead: {o.bmuLeadStatus}
                    </p>
                  </div>
                  <div className="text-right text-xs text-slate-500">
                    <div>{o._count.members} üye</div>
                    <div>{o._count.exams} sınav</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </main>
    </div>
  );
}

function CountCard({ label, value }: { label: string; value: number }): JSX.Element {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <p className="text-xs uppercase tracking-wider text-slate-500">{label}</p>
      <p className="mt-1 text-2xl font-bold text-slate-900">{value}</p>
    </div>
  );
}

function Row({ label, value }: { label: string; value: number | string }): JSX.Element {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-slate-700">{label}</span>
      <span className="font-mono font-medium text-slate-900">{value}</span>
    </div>
  );
}

function Detail({
  label,
  value,
  highlight,
}: {
  label: string;
  value: string;
  highlight?: boolean;
}): JSX.Element {
  return (
    <div>
      <p className="text-xs uppercase tracking-wider text-slate-500">{label}</p>
      <p
        className={`mt-1 text-3xl font-bold ${
          highlight ? 'text-green-700' : 'text-slate-900'
        }`}
      >
        {value}
      </p>
    </div>
  );
}
